<script setup lang="ts">
import { nextTick, onMounted, ref, watch } from "vue";

import debounce from "lodash/debounce";

import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";

interface DropdownInput {
    modelValue: string;
    placeholder?: string;
    noicon?: boolean;
    debounce?: number;
    focused?: boolean;
}
const props = withDefaults(defineProps<DropdownInput>(), {
    noicon: false,
    debounce: 0,
});

const emit = defineEmits(["update:modelValue", "mounted"]);
const isFocused = ref(false);

const handleChange = debounce((event: Event) => {
    emit("update:modelValue", event.target?.value);
}, props.debounce);

const handleReset = () => {
    // SetTimeout to prevent dropdown from closing on badge-x click
    setTimeout(() => {
        emit("update:modelValue", "");
    }, 0);
};

const input = ref();
watch(
    () => props.focused,
    async () => {
        if (input.value && props.focused) {
            // Ensure the input is rendered before focusing it
            await nextTick();
            (input.value as HTMLElement).focus();
        }
    }
);

onMounted(() => {
    emit("mounted");
});
</script>
<template>
    <div class="ds-dropdown-input" :class="{ focus: isFocused }">
        <i v-if="!noicon" :class="['ti ti-search', { focus: isFocused }]"></i>
        <input
            ref="input"
            :value="modelValue"
            @input="handleChange"
            :placeholder="placeholder"
            @focus="isFocused = true"
            @blur="isFocused = false"
            @click.stop
        />
        <ButtonAlt v-if="modelValue" icon-only="x" type="neutral" variant="ghost" size="xs" @click="handleReset" />
    </div>
</template>
<style scoped lang="scss">
.ds-dropdown-input {
    display: flex;
    gap: 6px;
    align-items: center;
    padding: 8px;
    border-radius: 8px;
    transition: 0.2s;

    &:hover,
    &.focus {
        background-color: $bg-weak-100;
        input {
            background-color: $bg-weak-100;
        }
    }
    i {
        font-size: 18px;
        transition: 0.2s;
        &.focus {
            color: $blue-base;
        }
    }
    input {
        border: none;
        outline: none;
        padding: 5px 0;
        color: $text-main-900;
        font-weight: 400;
        width: 100%;

        height: 20px;

        overflow: hidden;

        @include font-size-14;
        transition: 0.2s;
        &::placeholder {
            @include font-size-14;
        }
    }
}
</style>
